import { memo, useState, MouseEvent, useRef, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
//
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import DialogSimple from '@/components/dialog/DialogSimple';
import {
  VBMApplyCouponDialog,
  VBMBookingDetails,
  VBMCloneSessionDialog,
} from '..';
import { Iconify, Label } from '@/components';
import { TableMoreMenu } from '@/components/table';
// hooks + utils
import useToggle from '@/hooks/useToggle';
import useResponsive from '@/hooks/useResponsive';
import { useGetAccess } from '@/hooks/useAccess';
import useAuth from '@/hooks/useAuth';
import { removeSearchParam } from '@/utils/removeSearchParams';
import { fDate } from '@/utils/formatTime';
import { providersPath } from '@/features/users/providers';
import useDeleteBookingSession from '@/features/bookings-management/hooks/useDeleteBookingSession';
import {
  getBookingServiceIcon,
  isVoidBookingSessionStatus,
  resolveBookingSessionBillableHours,
} from '@/features/bookings-management/utils';
//
import { BookingSession } from '@/features/bookings-management/types';
import { getBookingSession } from '@/features/bookings-management/api';
import { LabelColors } from '@/types';

//
type VBMAccordionProps = {
  bookingSession: BookingSession;
  isExpanded?: boolean;
  hideSummary?: boolean;
  selected?: boolean;
  showSelection?: boolean;
  onSelect?: (
    bookingSessionId: number,
    event: MouseEvent<HTMLButtonElement>
  ) => void;
};

const formatHoursValue = (value: number) => {
  const roundedValue = Math.round(value * 100) / 100;
  return Number.isInteger(roundedValue)
    ? String(roundedValue)
    : String(roundedValue);
};

function VBMAccordion({
  bookingSession: initialBookingSession,
  isExpanded,
  hideSummary = false,
  selected = false,
  showSelection = false,
  onSelect,
}: VBMAccordionProps) {
  const accordionSummaryRef = useRef<HTMLDivElement | null>(null);
  const downMd = useResponsive('down', 'md');
  const downLg = useResponsive('down', 1500); // default lg value is 1200 but cleaners and status labels are messing up the layout from <1500
  const { canUpdate } = useGetAccess('bookings');
  const { isSuperAdmin } = useAuth();
  const { toggle: isAccordionExpanded, onToggle: toggleAccordion } = useToggle(
    !!isExpanded
  );
  const {
    toggle: isCloneSessionDialogOpen,
    onOpen: openCloneSessionDialog,
    onClose: closeCloneSessionDialog,
  } = useToggle();
  const {
    toggle: isCouponCodeDialogOpen,
    onOpen: openCouponCodeDialog,
    onClose: closeCouponCodeDialog,
  } = useToggle();
  const {
    toggle: isDeleteSessionDialogOpen,
    onOpen: openDeleteSessionDialog,
    onClose: closeDeleteSessionDialog,
  } = useToggle();

  // 3DOTMENU AND ACCORDION
  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const queryClient = useQueryClient();
  const { deleteSessionMutation } = useDeleteBookingSession({
    onSuccessFn: (bookingSessionId) => {
      closeDeleteSessionDialog();
      if (searchParams.get('sessionID') === String(bookingSessionId)) {
        removeSearchParam(searchParams, 'sessionID', setSearchParams);
      }
    },
  });

  useEffect(() => {
    queryClient.setQueryData(
      ['bookingSession', String(initialBookingSession.id)],
      initialBookingSession
    );
  }, [initialBookingSession, queryClient]);

  const { data: bookingSession } = useQuery({
    queryKey: ['bookingSession', String(initialBookingSession.id)],
    queryFn: () => getBookingSession(initialBookingSession.id),
    enabled: isAccordionExpanded,
    staleTime: 0,
  });

  const resolvedBookingSession = bookingSession ?? initialBookingSession;
  const canDeleteSession =
    isSuperAdmin === true &&
    isVoidBookingSessionStatus(resolvedBookingSession.booking_session_status);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // it's in the accordion header, it would affect the toggle
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation(); // it's in the accordion header, it would affect the toggle
    setOpenMenuActions(null);
  };

  const handleToggleAccordion = () => {
    !isAccordionExpanded
      ? setSearchParams({ sessionID: String(resolvedBookingSession.id) })
      : removeSearchParam(searchParams, 'sessionID', setSearchParams);
    toggleAccordion();
  };

  const handleSelectSession = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onSelect?.(resolvedBookingSession.id, event);
  };

  // DERIVED VALUES
  const sessionDateDisplayConfig = downLg ? 'EE, do MMM yy' : 'EE, dd MMM yyyy';
  const sessionTitleDateTime = `${fDate(
    resolvedBookingSession.date,
    sessionDateDisplayConfig
  )} - ${resolvedBookingSession.time}`;
  const totalSessionHours = resolveBookingSessionBillableHours(
    resolvedBookingSession
  );
  const sessionTitleTotalHours = downLg
    ? `${formatHoursValue(totalSessionHours)}hrs`
    : `${formatHoursValue(totalSessionHours)} HOURS`;
  const serviceIcon = getBookingServiceIcon(
    resolvedBookingSession.service_variant
  );
  const hasSessionTitleIcon =
    serviceIcon ||
    resolvedBookingSession.is_clone ||
    resolvedBookingSession.linen;
  const hasHighlightedSummary = isAccordionExpanded || selected;

  // STATUS AND CLEANER COMPONENT
  const renderStatusLabels = () => (
    <Stack
      direction={downLg ? 'column' : 'row'}
      spacing={downLg ? 1 : 2}
      sx={{ fontSize: 10 }}
    >
      <Label
        color={
          (resolvedBookingSession.booking_session_payment_status
            .style_class as LabelColors) ?? 'info'
        }
        sx={{ fontSize: downMd ? 11 : 14 }}
      >
        {resolvedBookingSession.booking_session_payment_status.display ?? 'N/A'}
      </Label>
      <Label
        color={
          (resolvedBookingSession.booking_session_status
            .style_class as LabelColors) ?? 'info'
        }
        sx={{ fontSize: downMd ? 11 : 14 }}
      >
        {resolvedBookingSession.booking_session_status.display ?? 'N/A'}
      </Label>
    </Stack>
  );

  const renderAssignedCleaner = () => (
    <Stack direction={downLg ? 'column' : 'row'} spacing={downLg ? 1 : 2}>
      {resolvedBookingSession.booking_session_cleaners.map((cleaner) => (
        <Chip
          label={
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {`${cleaner?.first_name ?? 'N/A'} ${cleaner?.last_name ?? 'N/A'}`}
              {cleaner?.seen_by_provider ? (
                <Iconify
                  icon={'eva:eye-fill'}
                  width={16}
                  height={16}
                  sx={{ color: 'success.dark', marginLeft: 1 }}
                  title="Seen"
                />
              ) : (
                <Iconify
                  icon={'eva:eye-fill'}
                  width={16}
                  height={16}
                  sx={{ opacity: 0.2, marginLeft: 1 }}
                  title="Not Seen"
                />
              )}
              {cleaner?.accepted_by_provider ? (
                <Iconify
                  icon={'eva:checkmark-circle-2-fill'}
                  width={16}
                  height={16}
                  sx={{ color: 'success.dark', marginLeft: 1 }}
                  title="Accepted"
                />
              ) : (
                <Iconify
                  icon={'eva:checkmark-circle-2-fill'}
                  width={16}
                  height={16}
                  sx={{ opacity: 0.2, marginLeft: 1 }}
                  title="Not Accepted"
                />
              )}
            </span>
          }
          key={cleaner?.id}
          color="default"
          sx={{ cursor: 'pointer', pointerEvents: 'auto' }}
          component={Link}
          to={providersPath('edit', {
            userId: String(cleaner.id),
            tabSid: 'details',
          })}
          size="small"
        />
      ))}
    </Stack>
  );

  // HANDLE CLONE + COUPON CODE
  const handleCloneSession = () => {
    openCloneSessionDialog();
    setOpenMenuActions(null);
  };

  const handleApplyCoupon = () => {
    openCouponCodeDialog();
    setOpenMenuActions(null);
  };

  const handleDeleteSession = () => {
    if (!canDeleteSession) return;
    openDeleteSessionDialog();
    setOpenMenuActions(null);
  };

  const handleConfirmDeleteSession = () => {
    if (!canDeleteSession) return;
    deleteSessionMutation.mutate(resolvedBookingSession.id);
  };

  // SCROLL TO EXPANDED ACCORDION
  const scrollToAccordion = () => {
    if (accordionSummaryRef.current) {
      accordionSummaryRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isExpanded) {
      timeoutId = setTimeout(() => {
        scrollToAccordion();
      }, 150);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isExpanded]);

  return (
    <>
      <Accordion expanded={isAccordionExpanded}>
        {!hideSummary && (
          <AccordionSummary
            expandIcon={<ExpandMoreIcon onClick={handleToggleAccordion} />}
            onClick={handleToggleAccordion}
            sx={{
              backgroundColor: hasHighlightedSummary
                ? 'primary.lighter'
                : 'inherit',
            }}
            ref={isAccordionExpanded ? accordionSummaryRef : null}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: '100%', mr: 1 }}
            >
              {/* TITLE */}
              <Stack direction="row" spacing={1.2} alignItems="center">
                {showSelection && (
                  <Checkbox
                    checked={selected}
                    disableRipple
                    onClick={handleSelectSession}
                    inputProps={{
                      'aria-label': `Select session ${resolvedBookingSession.id}`,
                    }}
                    sx={{ p: 0.5 }}
                  />
                )}
                <Box
                  sx={{
                    minWidth: hasSessionTitleIcon ? 44 : 20,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                  }}
                >
                  {resolvedBookingSession.is_clone && (
                    <Iconify
                      icon="flowbite:file-clone-solid"
                      sx={{ color: 'info.main' }}
                    />
                  )}
                  {serviceIcon && (
                    <Iconify
                      icon={serviceIcon.icon}
                      title={serviceIcon.title}
                      sx={serviceIcon.sx}
                    />
                  )}
                  {resolvedBookingSession.linen && (
                    <Iconify
                      icon="openmoji:bed-linen"
                      title="Linen"
                      sx={linenIconSx}
                    />
                  )}
                </Box>
                <Stack
                  direction={downMd ? 'column' : 'row'}
                  spacing={downMd ? 0.5 : 1.5}
                  divider={
                    downMd ? null : (
                      <span style={{ fontSize: 16, fontWeight: 600 }}> - </span>
                    )
                  }
                >
                  <Typography fontSize={downMd ? 14 : 16} fontWeight={600}>
                    #{resolvedBookingSession.id}
                  </Typography>
                  <Typography fontSize={downMd ? 12 : 16} fontWeight={600}>
                    {sessionTitleDateTime}
                  </Typography>
                  <Typography
                    fontSize={downMd ? 12 : 16}
                    fontWeight={600}
                    sx={{
                      backgroundColor: (theme) =>
                        hasHighlightedSummary
                          ? theme.palette.background.paper
                          : theme.palette.primary.lighter,
                      px: 1,
                      borderRadius: 1,
                    }}
                  >
                    {sessionTitleTotalHours}
                  </Typography>
                  {downMd && renderAssignedCleaner()}
                  {downMd && renderStatusLabels()}
                </Stack>
              </Stack>

              <Stack
                direction="row"
                spacing={downMd ? 2 : 4}
                alignItems="center"
              >
                {/* STATUS LABELS */}
                {!downMd && renderAssignedCleaner()}
                {!downMd && renderStatusLabels()}

                {/* 3-DOT-MENU */}
                <TableMoreMenu
                  open={openMenu}
                  onOpen={handleOpenMenu}
                  onClose={handleCloseMenu}
                  iconHeight={15}
                  iconWidth={15}
                  popOverWidth={170}
                  arrow="top-right"
                  actions={
                    <>
                      {canUpdate && (
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCloneSession();
                          }}
                        >
                          <Iconify icon={'clarity:clone-line'} />
                          Clone session
                        </MenuItem>
                      )}
                      {canUpdate && (
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApplyCoupon();
                          }}
                        >
                          <Iconify icon={'iconoir:percentage'} />
                          Apply Coupon
                        </MenuItem>
                      )}
                      {canDeleteSession && (
                        <MenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSession();
                          }}
                          sx={{ color: 'error.main' }}
                        >
                          <Iconify icon={'eva:trash-2-outline'} />
                          Delete
                        </MenuItem>
                      )}
                    </>
                  }
                />
              </Stack>
            </Stack>
          </AccordionSummary>
        )}
        <AccordionDetails>
          <VBMBookingDetails
            displayPaymentDetails
            sessionView
            bookingSession={resolvedBookingSession}
          />
        </AccordionDetails>
      </Accordion>
      {isCouponCodeDialogOpen && (
        <VBMApplyCouponDialog
          isDialogOpen={isCouponCodeDialogOpen}
          closeDialog={closeCouponCodeDialog}
          serviceVariantId={resolvedBookingSession.service_variant.id}
          bookingSessionId={resolvedBookingSession.id}
        />
      )}
      {isCloneSessionDialogOpen && (
        <VBMCloneSessionDialog
          isDialogOpen={isCloneSessionDialogOpen}
          closeDialog={closeCloneSessionDialog}
          currentBookingSession={resolvedBookingSession}
        />
      )}
      <DialogSimple
        dialogTitle={`Delete session #${resolvedBookingSession.id}?`}
        open={isDeleteSessionDialogOpen}
        onClickButtonClose={closeDeleteSessionDialog}
        onClickButtonLoading={handleConfirmDeleteSession}
        loadingButton
        loadingButtonText="Delete"
        loadingButtonColor="error"
        disagreeButton
        disagreeButtonText="Cancel"
        disagreeButtonColor="inherit"
        isSubmitting={deleteSessionMutation.isLoading}
      >
        <Typography sx={{ px: 3, pb: 2 }}>
          This will delete this booking session. This action cannot be undone.
        </Typography>
      </DialogSimple>
    </>
  );
}

export default memo(VBMAccordion);

const linenIconSx = {
  color: 'info.main',
  '& [fill]:not([fill="none"])': {
    fill: 'currentColor !important',
  },
  '& [stroke]:not([stroke="none"])': {
    stroke: 'currentColor !important',
  },
} as const;
